import { SnowplowSearchFilterItem } from "@themuse/snowplow-js-client";

import { museTrack } from "./tracking";
import { SearchFilters } from "../interfaces/context/searchContextInterfaces";
import { getRadioFacetValue } from "./searchFacets";

/**
 * Map search filter field names to oskar tracking fields.
 *
 * [SearchFilters.<type>]: <Oskar tracking field>
 */
const oskarFieldMapping = {
    companySize: "company_size",
    experienceLevel: "job_level",
    industry: "industry",
    perks: "perks_and_benefits",
    category: "job_categories",
    work_location: "remote_work_location_type",
    radius: "radius",
    postedDateRange: "date_posted",
    companyLeadership: "company_leadership",
    diversityAndInclusion: "diversity_and_inclusion"
};

const snowplowFilterNameMap = new Map<string, string>();
snowplowFilterNameMap
    .set("category", "Job Category")
    .set("companyLeadership", "Diversity & Inclusion")
    .set("companySize", "Company Size")
    .set("experienceLevel", "Experience Level")
    .set("industry", "Company Industry")
    .set("perks", "Perks & Benefits")
    .set("postedDateRange", "Date Posted")
    .set("radius", "Search Radius")
    .set("company", "Company Name")
    .set("location", "Location");

/**
 * Triggers muse track events for Applied / Removed filters.
 *
 * To support additional facets, add field mapping to oskarFieldMapping.
 */
export const trackFacets = (
    eventName: "Applied Filter" | "Removed Filter",
    filterSource: string,
    sourceFilters: SearchFilters,
    diffFilters: SearchFilters
) => {
    Object.keys(sourceFilters).forEach((key: string) => {
        if (Array.isArray(sourceFilters[key])) {
            sourceFilters[key].forEach((filterItem) => {
                if (oskarFieldMapping[key] && !diffFilters[key]?.includes(filterItem)) {
                    museTrack(eventName, {
                        source: "search-listing",
                        model_type: "jobs",
                        filter_source: filterSource,
                        name: oskarFieldMapping[key],
                        value: filterItem
                    });
                }
            });
        }
        if (typeof sourceFilters[key] === "string") {
            if (sourceFilters[key] !== diffFilters[key]) {
                museTrack(eventName, {
                    source: "search-listing",
                    model_type: "jobs",
                    filter_source: filterSource,
                    name: oskarFieldMapping[key],
                    value: getRadioFacetValue(key, sourceFilters[key])
                });
            }
        }
    });
};

export const buildSnowplowFilterItems = (filters: SearchFilters, location?: string, company?: string) => {
    const items: SnowplowSearchFilterItem[] = [];
    const modifiedFilters = { ...filters };
    if (location) {
        Object.assign(modifiedFilters, { location: [location] });
    }
    if (filters.location && !location) {
        delete modifiedFilters.location;
    }
    if (company) {
        Object.assign(modifiedFilters, { company: [company] });
    }
    if (filters.company && !company) {
        delete modifiedFilters.company;
    }
    Object.keys(modifiedFilters).forEach((key) => {
        if (!snowplowFilterNameMap.get(key) || !modifiedFilters[key].length) return;
        if (Array.isArray(modifiedFilters[key])) {
            modifiedFilters[key].forEach((val: string) => {
                items.push({
                    filter_name: snowplowFilterNameMap.get(key),
                    filter_value: val
                });
            });
        } else {
            items.push({
                filter_name: snowplowFilterNameMap.get(key),
                filter_value: modifiedFilters[key]
            });
        }
    });
    return items;
};
